'use client';

export interface StripePaymentResult {
  success: boolean;
  transactionId?: string;
  last4?: string;
  brand?: string;
  error?: string;
  declineCode?: string;
}

// Simulate Stripe payment intent creation and confirmation
export async function simulateStripePayment(
  cardNumber: string,
  expiry: string,
  cvv: string,
  cardName: string,
  amount: number
): Promise<StripePaymentResult> {
  // Simulate network delay (Stripe-like response time)
  await new Promise((r) => setTimeout(r, 1800 + Math.random() * 600));

  const rawCard = cardNumber.replace(/\s/g, '');
  const last4 = rawCard.slice(-4);

  // Simulate Stripe test card behaviors
  // 4000000000000002 → card_declined
  if (rawCard === '4000000000000002') {
    return { success: false, error: 'Your card was declined.', declineCode: 'card_declined' };
  }
  // 4000000000009995 → insufficient_funds
  if (rawCard === '4000000000009995') {
    return { success: false, error: 'Your card has insufficient funds.', declineCode: 'insufficient_funds' };
  }
  // 4000000000000069 → expired_card
  if (rawCard === '4000000000000069') {
    return { success: false, error: 'Your card has expired.', declineCode: 'expired_card' };
  }
  // 4000000000000127 → incorrect_cvc
  if (rawCard === '4000000000000127') {
    return { success: false, error: 'Your card\'s security code is incorrect.', declineCode: 'incorrect_cvc' };
  }

  // Detect card brand from first digit
  let brand = 'Visa';
  if (rawCard.startsWith('5') || rawCard.startsWith('2')) brand = 'Mastercard';
  else if (rawCard.startsWith('3')) brand = 'Amex';
  else if (rawCard.startsWith('6')) brand = 'Discover';

  // Generate a realistic-looking transaction ID
  const txnId = `pi_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  // Store transaction in localStorage for admin portal visibility
  if (typeof window !== 'undefined') {
    try {
      const existing = JSON.parse(localStorage.getItem('fca_transactions') || '[]');
      existing.push({
        transactionId: txnId,
        amount,
        status: 'succeeded',
        date: new Date().toISOString().split('T')[0],
        last4,
        brand,
        cardName,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem('fca_transactions', JSON.stringify(existing));
    } catch {}
  }

  return {
    success: true,
    transactionId: txnId,
    last4,
    brand,
  };
}
